import { Channel } from '@flowus/common/block/channel';
import { useFinalValue, useScheduleUpdate } from '@flowus/common/hooks/react-utils';
import type { NotificationDTO } from '@next-space/fe-api-idl';
import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { LoadingContainer } from 'src/common/components/loading-container';
import { SPECIAL_SPACE_ID, UUID_NAMESPACE } from 'src/common/const';
import { request } from 'src/common/request';
import { EditableNamespace } from 'src/editor/editor/uikit/editable-context';
import { useCurrentSpace } from 'src/hooks/space';
import { blocksActions } from 'src/redux/reducers/blocks';
import { commentsActions } from 'src/redux/reducers/comments';
import { discussionsActions } from 'src/redux/reducers/discussions';
import { dispatch } from 'src/redux/store';
import type { NextBlock } from 'src/redux/types';
import { invalidateNotifications, useNotificationsMagic } from 'src/services/app';
import { updatePageActivities } from 'src/services/page-activities/hook';
import { v4 as uuid4 } from 'uuid';
import { PageScene, PageSceneContext } from '../../scene-context';
import { NotificationItems } from './notification-items';

const PER_PAGE = 20;

export const Notifications: FC<{ items: NotificationDTO[]; archived: boolean }> = ({
  archived,
  items,
}) => {
  return (
    <PageSceneContext.Provider value={PageScene.NOTIFICATION}>
      <EditableNamespace.Provider value={UUID_NAMESPACE.PAGE_FEEDS}>
        {items
          .filter((it) => {
            return (it.archived ?? false) === archived;
          })
          .map((it) => (
            <NotificationItems key={it.uuid} data={it} />
          ))}
      </EditableNamespace.Provider>
    </PageSceneContext.Provider>
  );
};

export const NotificationBox: FC<{ channel: Channel }> = (props) => {
  const { channel } = props;
  const currentSpace = useCurrentSpace();
  const state = useFinalValue(() => ({
    uuid: uuid4(),
    hasMore: true,
    nextPage: 1,
    pending: false,
    items: [] as NotificationDTO[],
  }));
  const notificationMagic = useNotificationsMagic();
  const scheduleUpdate = useScheduleUpdate();
  const loadMore = useCallback(async () => {
    if (state.pending) return;
    let spaceId = currentSpace.uuid;

    if (channel === Channel.SUBSCRIBE) {
      spaceId = SPECIAL_SPACE_ID;
    }

    const page = state.nextPage;
    state.pending = true;
    const result = await request.editor.getNotificationList(
      spaceId,
      channel,
      state.nextPage,
      PER_PAGE
    );
    if (page === state.nextPage) {
      if (result.recordMap?.blocks) {
        dispatch(
          blocksActions.update({
            blocks: result.recordMap.blocks as Record<string, NextBlock>,
          })
        );
      }
      if (result.recordMap?.activities) {
        updatePageActivities(result.recordMap.activities);
      }
      if (result.recordMap?.discussions) {
        dispatch(discussionsActions.update(result.recordMap.discussions));
      }
      if (result.recordMap?.comments) {
        dispatch(commentsActions.update(result.recordMap.comments));
      }
      if (page === 1) {
        state.items = [];
      }
      const items = (result.notificationIds ?? [])
        .map((it: string) => {
          return result.recordMap?.notifications?.[it];
        })
        .filter((it: NotificationDTO | undefined) => it != null) as NotificationDTO[];
      state.items.push(...items);
      state.nextPage = page + 1;
      state.pending = false;
      // TODO 超过10页就不继续加载了
      state.hasMore = state.items.length >= 100 ? false : result.more ?? false;
      scheduleUpdate();
    }
  }, [channel, currentSpace.uuid, scheduleUpdate, state]);

  const [sentryRef] = useInfiniteScroll({
    loading: state.pending,
    hasNextPage: state.hasMore,
    onLoadMore: loadMore,
  });

  useEffect(() => {
    state.uuid = uuid4();
    state.nextPage = 1;
    state.hasMore = true;
    state.pending = false;
    void loadMore();
  }, [notificationMagic, loadMore, state]);

  useEffect(() => {
    return () => {
      let spaceId = currentSpace.uuid;

      if (channel === Channel.SUBSCRIBE) {
        spaceId = SPECIAL_SPACE_ID;
      }

      void request.editor
        .setNotificationRead({
          spaceId,
          channel,
          timestamp: Date.now(),
        })
        .then(() => {
          invalidateNotifications();
        });
    };
  }, [channel, currentSpace.uuid, state.items]);
  return (
    <>
      <Notifications items={state.items} archived={false} />
      {(state.pending || state.hasMore) && (
        <LoadingContainer size="middle" ref={sentryRef} className="mt-1.5 py-2.5" />
      )}
    </>
  );
};
